import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, MapPin, MessageCircle, ExternalLink } from "lucide-react";
import SEO from "@/components/SEO";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [settings, setSettings] = useState({});

  useEffect(() => { api.get("/settings").then(({data}) => setSettings(data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error("Could not send message"); }
    finally { setSending(false); }
  };

  const waNumber = (settings.whatsapp_number || "").replace(/\D/g, "");
  const waLink = waNumber ? `https://wa.me/${waNumber}` : "";

  return (
    <div className="container-x py-16 max-w-5xl" data-testid="contact-page">
      <SEO title="Contact AdiYogi Handicrafts" description="Reach out on WhatsApp, email or through our form. Visit our Google Maps location — pan-India and worldwide shipping." />
      <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">Get in touch</div>
      <h1 className="font-serif text-4xl md:text-6xl mb-8">Visit AdiYogi Handicrafts</h1>

      <div className="grid md:grid-cols-2 gap-10">
        <form onSubmit={submit} className="space-y-3">
          <input required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} placeholder="Full name" className="w-full border hairline rounded-lg px-3 py-2 bg-white outline-none focus:border-[#B8860B]" data-testid="ct-name" />
          <input required type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} placeholder="Email" className="w-full border hairline rounded-lg px-3 py-2 bg-white outline-none focus:border-[#B8860B]" data-testid="ct-email" />
          <input value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} placeholder="Phone / WhatsApp" className="w-full border hairline rounded-lg px-3 py-2 bg-white outline-none focus:border-[#B8860B]" data-testid="ct-phone" />
          <textarea required value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})} placeholder="How can we help?" rows={5} className="w-full border hairline rounded-lg px-3 py-2 bg-white outline-none focus:border-[#B8860B]" data-testid="ct-message" />
          <button disabled={sending} className="btn-gold rounded-full px-6 py-3 text-xs uppercase tracking-widest" data-testid="ct-submit">{sending ? "Sending…" : "Send Message"}</button>
        </form>

        <div className="space-y-3 text-sm">
          <div className="border hairline rounded-xl p-5 bg-white flex items-start gap-3">
            <Mail className="w-5 h-5 gold-text mt-0.5" strokeWidth={1.5} />
            <div><div className="font-medium">Email</div><div className="text-muted-foreground">{settings.email ? <a href={`mailto:${settings.email}`} className="hover:gold-text hover:underline" data-testid="contact-email-link">{settings.email}</a> : "—"}</div></div>
          </div>
          <div className="border hairline rounded-xl p-5 bg-white flex items-start gap-3">
            <Phone className="w-5 h-5 gold-text mt-0.5" strokeWidth={1.5} />
            <div><div className="font-medium">Phone</div><div className="text-muted-foreground">{settings.phone ? <a href={`tel:${String(settings.phone).replace(/[^+\d]/g, "")}`} className="hover:gold-text hover:underline" data-testid="contact-phone-link">{settings.phone}</a> : "—"}</div></div>
          </div>
          <div className="border hairline rounded-xl p-5 bg-white flex items-start gap-3">
            <MapPin className="w-5 h-5 gold-text mt-0.5" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="font-medium">AdiYogi Handicrafts</div>
              <div className="text-muted-foreground text-xs">{settings.business_hours}</div>
              {settings.google_maps_url && (
                <a href={settings.google_maps_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs gold-text hover:underline" data-testid="get-directions">
                  Get Directions <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                </a>
              )}
            </div>
          </div>
          <div className="border hairline rounded-xl p-5 bg-white flex items-start gap-3">
            <MessageCircle className="w-5 h-5 text-emerald-600 mt-0.5" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="font-medium">WhatsApp</div>
              <div className="text-muted-foreground text-xs mb-2">Chat with us for quick queries or browse our WhatsApp catalogue.</div>
              <div className="flex flex-wrap gap-2">
                {waLink && (
                  <a href={waLink} target="_blank" rel="noopener noreferrer" className="rounded-full border border-emerald-600 text-emerald-700 px-4 py-1.5 text-xs uppercase tracking-widest hover:bg-emerald-600 hover:text-white transition" data-testid="wa-chat">Chat on WhatsApp</a>
                )}
                {settings.whatsapp_catalogue_url && (
                  <a href={settings.whatsapp_catalogue_url} target="_blank" rel="noopener noreferrer" className="rounded-full bg-emerald-600 text-white px-4 py-1.5 text-xs uppercase tracking-widest hover:bg-emerald-700 transition" data-testid="wa-catalogue">View WhatsApp Catalogue</a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* HEAD OFFICES */}
      <section className="mt-16" data-testid="head-offices">
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">Head Offices</div>
        <h2 className="font-serif text-3xl md:text-4xl mb-8">Visit us — Mumbai &amp; Coimbatore</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="border hairline rounded-xl p-6 bg-white" data-testid="office-mumbai">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 shrink-0 rounded-full border border-[#B8860B] flex items-center justify-center">
                <MapPin className="w-5 h-5 gold-text" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Head Office</div>
                <div className="font-serif text-2xl">Mumbai</div>
                <p className="text-sm text-muted-foreground mt-3">
                  Madame Cama Rd, Dr Ambedkar Statue Chowk Area, Colaba,
                  <br />Mumbai, Maharashtra 400001
                </p>
                <a href="https://www.google.com/maps/search/?api=1&query=Madame+Cama+Rd+Dr+Ambedkar+Statue+Chowk+Colaba+Mumbai+400001" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 text-xs gold-text hover:underline">
                  Get Directions <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                </a>
              </div>
            </div>
          </div>
          <div className="border hairline rounded-xl p-6 bg-white" data-testid="office-coimbatore">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 shrink-0 rounded-full border border-[#B8860B] flex items-center justify-center">
                <MapPin className="w-5 h-5 gold-text" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Head Office</div>
                <div className="font-serif text-2xl">Coimbatore</div>
                <p className="text-sm text-muted-foreground mt-3">
                  Velliangiri Foothills, Mahashivaratri Grounds, Ishana Vihar,
                  <br />Coimbatore, Tamil Nadu 641114
                </p>
                <a href="https://www.google.com/maps/search/?api=1&query=Velliangiri+Foothills+Mahashivaratri+Grounds+Ishana+Vihar+Coimbatore+641114" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 text-xs gold-text hover:underline">
                  Get Directions <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="delivery-banner">
          <div className="border hairline rounded-xl p-5 bg-muted/40 flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-white border hairline flex items-center justify-center text-xl" aria-hidden="true">🚚</div>
            <div>
              <div className="font-medium">All Over India Delivery</div>
              <div className="text-xs text-muted-foreground">Pan-India shipping from our Mumbai &amp; Coimbatore hubs.</div>
            </div>
          </div>
          <div className="border hairline rounded-xl p-5 bg-muted/40 flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-white border hairline flex items-center justify-center text-xl" aria-hidden="true">⚡</div>
            <div>
              <div className="font-medium">Fast &amp; Secure Delivery</div>
              <div className="text-xs text-muted-foreground">Prepaid orders dispatched quickly with trusted couriers.</div>
            </div>
          </div>
        </div>

        {/* DELIVERY TIMELINES */}
        <div className="mt-8" data-testid="delivery-timelines">
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">Estimated Delivery</div>
          <h3 className="font-serif text-2xl md:text-3xl mb-5">City-wise timelines</h3>
          <div className="border hairline rounded-xl bg-white overflow-hidden">
            <div className="grid grid-cols-2 md:grid-cols-3 divide-x divide-y hairline text-sm">
              {[
                { zone: "All India (Metros)", days: "3–4 days", note: "Dispatched from Mumbai / Coimbatore hubs" },
                { zone: "All India (Tier 2 & 3)", days: "3–4 days", note: "Standard express" },
                { zone: "J&K · Ladakh · Andaman", days: "6–8 days", note: "Remote pincodes" },
                { zone: "Metro Same-Day Pincodes", days: "Contact us", note: "On WhatsApp before order" },
                { zone: "Worldwide", days: "10–21 days", note: "Country-specific rates at checkout" },
              ].map((r) => (
                <div key={r.zone} className="p-4">
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{r.zone}</div>
                  <div className="font-serif text-lg gold-text mt-1">{r.days}</div>
                  <div className="text-xs text-muted-foreground mt-1">{r.note}</div>
                </div>
              ))}
            </div>
            <div className="border-t hairline px-4 py-3 text-[11px] text-muted-foreground bg-muted/30">
              Timelines are indicative business-day estimates after payment verification. Actual delivery may vary based on pincode serviceability and courier availability. Prepaid orders only.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
